
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TicketComment } from "@/lib/types/support-tickets";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/context/auth-context";
import { LockIcon } from "lucide-react";

interface TicketCommentListProps {
  comments: TicketComment[];
  isLoading?: boolean;
}

const TicketCommentList: React.FC<TicketCommentListProps> = ({
  comments,
  isLoading = false
}) => {
  const { profile } = useAuth();
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'tech';
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24 ml-auto" />
              </div>
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  if (comments.length === 0) {
    return <p className="text-center text-muted-foreground py-4">No comments yet.</p>;
  }
  
  return (
    <div className="space-y-4">
      {comments.map((comment) => {
        // Skip internal comments for non-staff users
        if (comment.is_internal && !isStaff) {
          return null;
        }
        
        const formattedDate = format(new Date(comment.created_at), "MMM d, yyyy 'at' h:mm a");
        
        return (
          <Card 
            key={comment.id} 
            className={`overflow-hidden ${comment.is_internal ? 'border-amber-200 bg-amber-50' : ''}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold">{comment.user_name}</h4>
                  {comment.is_internal && (
                    <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs">
                      <LockIcon className="h-3 w-3" />
                      <span>Internal Note</span>
                    </div>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {formattedDate}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default TicketCommentList;
